<template>
  <div class="image-generation-root">
    <v-dialog :value="value" fullscreen hide-overlay transition="fade-transition" @input="onDialogInput">
      <div class="image-generation-dialog" @click.self="cancel">
        <div
          class="image-generation-dialog__panel"
          :class="{
            'image-generation-dialog__panel--concept': isConceptMode,
            'image-generation-dialog__panel--edit': isEditMode,
            'image-generation-dialog__panel--redesign': isRedesignMode,
          }"
          role="dialog"
          :aria-labelledby="titleId"
          @click.stop
        >
          <div class="image-generation-dialog__topbar">
            <h2 :id="titleId" class="image-generation-dialog__title">{{ modalTitle }}</h2>
            <v-btn icon title="Close" @click="cancel">
              <v-icon size="20">mdi-close</v-icon>
            </v-btn>
          </div>

          <section class="image-generation-content">
            <div class="image-generation-dialog__current-model">
              Images are currently generated using <span class="font-weight-bold">ChatGPT</span> model.
            </div>
            <!-- <div v-if="isConceptMode" class="image-generation-dialog__concept">
              <p class="image-generation-dialog__concept-label">Concept</p>
              <p class="image-generation-dialog__concept-title">{{ conceptTitle }}</p>
            </div> -->
            <input
              ref="productFileInput"
              type="file"
              class="image-generation-dialog__file-input"
              accept="image/png,image/jpeg,image/webp"
              @change="onProductFilesSelected"
            />
            <input
              v-if="isRedesignMode || isEditMode"
              ref="refFileInput"
              type="file"
              class="image-generation-dialog__file-input"
              accept="image/png,image/jpeg,image/webp"
              @change="onRefFilesSelected"
            />
            <!-- Redesign & edit mode -->
            <template v-if="isRedesignMode || isEditMode">
              <p v-if="isRedesignMode" class="redesign-compare__hint">
                Add your product image and a reference image to compare styles before redesigning.
              </p>
              <div class="redesign-compare" :class="{ 'redesign-compare--edit': isEditMode }">
                <div class="redesign-compare__column" v-if="isRedesignMode">
                  <div class="redesign-compare__column-head">
                    <v-icon size="18" class="redesign-compare__column-icon redesign-compare__column-icon--product">
                      mdi-package-variant-closed
                    </v-icon>
                    <div>
                      <p class="redesign-compare__column-label">Product image</p>
                      <p class="redesign-compare__column-desc">Image to redesign</p>
                    </div>
                  </div>
                  <div
                    class="image-generation-content__upload-zone redesign-compare__upload-zone"
                    role="button"
                    tabindex="0"
                    @click="triggerProductUpload"
                    @keydown.enter.prevent="triggerProductUpload"
                    @keydown.space.prevent="triggerProductUpload"
                  >
                    <v-icon size="24" class="mb-1">mdi-cloud-upload-outline</v-icon>
                    <div class="upload-zone__title">
                      {{ productSlotFilled ? 'Replace image' : 'Upload image' }}
                    </div>
                    <div class="upload-zone__subtitle">{{ uploadZoneSubtitle }}</div>
                  </div>
                  <div class="text-center">or</div>
                  <div class="upload-zone__url-buttons">
                    <v-btn
                      outlined
                      small
                      class="redesign-compare__url-btn"
                      :disabled="productSlotFilled"
                      @click="openSelectProductImageDialog('product')"
                    >
                      <v-icon left size="16">mdi-image-outline</v-icon>
                      Select product image
                    </v-btn>
                    <v-btn
                      outlined
                      small
                      class="redesign-compare__url-btn"
                      :disabled="productSlotFilled"
                      @click="openAddMediaFromUrlDialog('product')"
                    >
                      <v-icon left size="16">mdi-link-variant</v-icon>
                      Add from URL
                    </v-btn>
                  </div>
                  <div v-if="attachments.length" class="redesign-compare__preview">
                    <div v-for="att in attachments" :key="att.id" class="redesign-compare__thumb-wrap">
                      <button
                        type="button"
                        class="redesign-compare__thumb"
                        aria-label="Preview image"
                        title="Preview image"
                        :disabled="att.status !== 'ready'"
                        @click="openPreview(att)"
                      >
                        <img class="redesign-compare__thumb-img" :src="att.url" :alt="att.name" />
                        <div v-if="att.status === 'uploading'" class="attachment__overlay" aria-label="Uploading">
                          <v-progress-circular indeterminate size="22" width="2" color="white" />
                        </div>
                        <div
                          v-else-if="att.status === 'error'"
                          class="attachment__overlay attachment__overlay--error"
                          aria-label="Upload failed"
                        >
                          <v-icon size="20" color="white">mdi-alert-circle</v-icon>
                        </div>
                      </button>
                      <button
                        type="button"
                        class="attachment__remove"
                        aria-label="Remove image"
                        title="Remove image"
                        @click="removeAttachment(att.id)"
                      >
                        <v-icon size="14">mdi-close</v-icon>
                      </button>
                    </div>
                  </div>
                  <div v-else class="redesign-compare__empty">
                    <v-icon size="32" color="rgba(0,0,0,0.2)">mdi-image-outline</v-icon>
                  </div>
                </div>

                <div v-if="isRedesignMode" class="redesign-compare__divider" aria-hidden="true">
                  <span class="redesign-compare__vs">vs</span>
                </div>

                <div class="redesign-compare__column">
                  <div class="redesign-compare__column-head">
                    <v-icon size="18" class="redesign-compare__column-icon redesign-compare__column-icon--ref">
                      mdi-palette-outline
                    </v-icon>
                    <div>
                      <p class="redesign-compare__column-label">Reference image</p>
                      <p class="redesign-compare__column-desc">Style or layout to follow</p>
                    </div>
                  </div>
                  <div
                    class="image-generation-content__upload-zone redesign-compare__upload-zone"
                    role="button"
                    tabindex="0"
                    @click="triggerRefUpload"
                    @keydown.enter.prevent="triggerRefUpload"
                    @keydown.space.prevent="triggerRefUpload"
                  >
                    <v-icon size="24" class="mb-1">mdi-cloud-upload-outline</v-icon>
                    <div class="upload-zone__title">
                      {{ refSlotFilled ? 'Replace image' : 'Upload image' }}
                    </div>
                    <div class="upload-zone__subtitle">{{ uploadZoneSubtitle }}</div>
                  </div>
                  <div class="text-center">or</div>
                  <div class="upload-zone__url-buttons">
                    <v-btn
                      outlined
                      small
                      class="redesign-compare__url-btn"
                      :disabled="refSlotFilled"
                      @click="openSelectProductImageDialog('reference')"
                    >
                      <v-icon left size="16">mdi-image-outline</v-icon>
                      Select product image
                    </v-btn>
                    <v-btn
                      outlined
                      small
                      class="redesign-compare__url-btn"
                      :disabled="refSlotFilled"
                      @click="openAddMediaFromUrlDialog('reference')"
                    >
                      <v-icon left size="16">mdi-link-variant</v-icon>
                      Add from URL
                    </v-btn>
                  </div>
                  <div v-if="imageRef.length" class="redesign-compare__preview">
                    <div v-for="att in imageRef" :key="att.id" class="redesign-compare__thumb-wrap">
                      <button
                        type="button"
                        class="redesign-compare__thumb"
                        aria-label="Preview image"
                        title="Preview image"
                        :disabled="att.status !== 'ready'"
                        @click="openPreview(att)"
                      >
                        <img class="redesign-compare__thumb-img" :src="att.url" :alt="att.name" />
                        <div v-if="att.status === 'uploading'" class="attachment__overlay" aria-label="Uploading">
                          <v-progress-circular indeterminate size="22" width="2" color="white" />
                        </div>
                        <div
                          v-else-if="att.status === 'error'"
                          class="attachment__overlay attachment__overlay--error"
                          aria-label="Upload failed"
                        >
                          <v-icon size="20" color="white">mdi-alert-circle</v-icon>
                        </div>
                      </button>
                      <button
                        type="button"
                        class="attachment__remove"
                        aria-label="Remove"
                        @click="removeRefAttachment(att.id)"
                      >
                        <v-icon size="14">mdi-close</v-icon>
                      </button>
                    </div>
                  </div>
                  <div v-else class="redesign-compare__empty">
                    <v-icon size="32" color="rgba(0,0,0,0.2)">mdi-image-outline</v-icon>
                  </div>
                </div>
              </div>
            </template>

            <!-- Image concept mode -->
            <template v-if="isConceptMode">
              <div class="image-generation-content__upload-toolbar">
                <div
                  class="image-generation-content__upload-zone"
                  role="button"
                  tabindex="0"
                  @click="triggerProductUpload"
                  @keydown.enter.prevent="triggerProductUpload"
                  @keydown.space.prevent="triggerProductUpload"
                >
                  <v-icon size="28" class="mb-1">mdi-cloud-upload-outline</v-icon>
                  <div class="upload-zone__title">
                    {{ productSlotFilled ? 'Replace image' : 'Upload image' }}
                  </div>
                  <div class="upload-zone__subtitle">{{ uploadZoneSubtitle }}</div>
                </div>
                <div class="text-center">or</div>
                <div class="upload-zone__url-buttons">
                  <v-btn
                    outlined
                    small
                    class="redesign-compare__url-btn"
                    :disabled="productSlotFilled"
                    @click="openSelectProductImageDialog('product')"
                  >
                    <v-icon left size="16">mdi-image-outline</v-icon>
                    Select product image
                  </v-btn>
                  <v-btn
                    outlined
                    class="image-generation-content__url-btn"
                    height="auto"
                    :disabled="productSlotFilled"
                    @click="openAddMediaFromUrlDialog('product')"
                  >
                    <v-icon left size="18">mdi-link-variant</v-icon>
                    Add from URL
                  </v-btn>
                </div>
              </div>

              <div v-if="attachments.length" class="image-generation-content__attachments">
                <div class="attachments-strip" role="list" aria-label="Attachments">
                  <div v-for="att in visibleAttachments" :key="att.id" class="attachment" role="listitem">
                    <button
                      type="button"
                      class="attachment__thumb"
                      aria-label="Preview image"
                      title="Preview image"
                      :disabled="att.status !== 'ready'"
                      @click="openPreview(att)"
                    >
                      <img class="attachment__img" :src="att.url" :alt="att.name" />
                      <div v-if="att.status === 'uploading'" class="attachment__overlay" aria-label="Uploading">
                        <v-progress-circular indeterminate size="18" width="2" color="white" />
                      </div>
                      <div
                        v-else-if="att.status === 'error'"
                        class="attachment__overlay attachment__overlay--error"
                        aria-label="Upload failed"
                      >
                        <v-icon size="18" color="white">mdi-alert-circle</v-icon>
                      </div>
                    </button>
                    <button
                      type="button"
                      class="attachment__remove"
                      aria-label="Remove attachment"
                      @click="removeAttachment(att.id)"
                    >
                      <v-icon size="14">mdi-close</v-icon>
                    </button>
                  </div>

                  <button
                    v-if="hiddenCount > 0"
                    type="button"
                    class="attachments-more"
                    :aria-label="`Show ${hiddenCount} more`"
                    :title="`Show ${hiddenCount} more`"
                    @click="showAllAttachments = true"
                  >
                    +{{ hiddenCount }}
                  </button>
                </div>
              </div>
            </template>

            <div>
              <p class="image-generation-content__prompt-label">Your prompt</p>
              <textarea
                ref="messageInput"
                v-model="message"
                class="image-generation-content__textarea"
                :class="{ 'image-generation-content__textarea--redesign': isRedesignMode || isEditMode }"
                rows="6"
                :placeholder="textareaPlaceholder"
                spellcheck="false"
                autocorrect="off"
                autocomplete="off"
                autocapitalize="off"
              />
            </div>
          </section>

          <div class="image-generation-dialog__actions">
            <v-btn text class="image-generation-dialog__action-btn" @click="cancel">Cancel</v-btn>
            <v-btn
              color="primary"
              depressed
              class="image-generation-dialog__action-btn"
              :disabled="!canApply"
              @click="onSubmit"
            >
              {{ modelConfirmButtonText }}
            </v-btn>
          </div>
        </div>
      </div>
    </v-dialog>

    <PreviewImage
      v-if="preview.src && !preview.isVideo"
      :src="preview.src"
      :show-preview="preview.show"
      @close="closePreview"
    />

    <AddMediaFromUrlDialog
      v-model="showAddMediaUrlModal"
      media-types="image"
      :max-attachments="maxAttachments"
      :attachments-count="urlDialogCurrentCount"
      @save="onSaveMediaFromUrlDialog"
    />

    <SelectProductImage v-model="showSelectProductImageModal" :product="product" @select="onSelectProductImage" />
  </div>
</template>

<script>
import axios from 'axios';
import attachmentUploadMixin from './utils/attachment-upload-mixin';
import PreviewImage from './PreviewImage.vue';
import AddMediaFromUrlDialog from './AddMediaFromUrlDialog.vue';
import SelectProductImage from './SelectProductImage.vue';
import { filenameFromMediaUrl } from './utils';
import { uploadService } from '@/apis/upload.s';
import imageHelpers from '@/helpers/image';

export default {
  name: 'ImageGeneration',
  components: { PreviewImage, AddMediaFromUrlDialog, SelectProductImage },
  mixins: [attachmentUploadMixin],
  props: {
    value: { type: Boolean, default: false },
    mode: {
      type: String,
      default: 'edit',
      validator: v => ['edit', 'redesign', 'concept'].includes(v),
    },
    concept: {
      type: Object,
      default: null,
    },
    product: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      message: '',
      imageRef: [],
      urlDialogTarget: 'product',
      preview: {
        show: false,
        src: '',
        isVideo: false,
      },
      showAddMediaUrlModal: false,
      showSelectProductImageModal: false,
      selectProductImageTarget: 'product',
      titleId: 'image-generation-dialog-title',
    };
  },
  computed: {
    isRedesignMode() {
      return this.mode === 'redesign';
    },
    isEditMode() {
      return this.mode === 'edit';
    },
    isConceptMode() {
      return this.mode === 'concept';
    },
    conceptTitle() {
      return String(this.concept?.title || '').trim();
    },
    modalTitle() {
      if (this.isConceptMode) return 'Create ads image from concept';
      if (this.isRedesignMode) return 'Redesign ads image';
      return 'Create ads image';
    },
    modelConfirmButtonText() {
      if (this.isConceptMode) return 'Use this concept';
      if (this.isRedesignMode) return 'Redesign ads image';
      return 'Create ads image';
    },
    textareaPlaceholder() {
      if (this.isRedesignMode) {
        return 'Describe how the product image should match the reference style';
      }
      if (this.isConceptMode) return 'Describe how you want to create ads image from concept';
      return 'Describe how you want to change the ads image';
    },
    productSlotFilled() {
      return this.attachments.length >= 1;
    },
    refSlotFilled() {
      return this.imageRef.length >= 1;
    },
    hasReadyProductAttachment() {
      return this.hasReadyInList(this.attachments);
    },
    hasReadyRefAttachment() {
      return this.hasReadyInList(this.imageRef);
    },
    hasReadyImageAttachment() {
      return this.hasReadyProductAttachment;
    },
    isUploadingImageRef() {
      return Array.isArray(this.imageRef) && this.imageRef.some(a => a && a.status === 'uploading');
    },
    isUploadingAny() {
      return this.isUploadingAttachments || this.isUploadingImageRef;
    },
    canApply() {
      if (this.isUploadingAny) return false;
      if (!this.message.trim().length) return false;
      if (this.isRedesignMode) {
        return this.hasReadyProductAttachment && this.hasReadyRefAttachment;
      }
      if (this.isEditMode) {
        return this.hasReadyRefAttachment;
      }
      return this.hasReadyImageAttachment;
    },
    maxAttachments() {
      return 1;
    },
    uploadZoneSubtitle() {
      return `Up to ${this.maxAttachments} ${this.maxAttachments > 1 ? 'images' : 'image'}, max 5MB ${
        this.maxAttachments > 1 ? 'each' : ''
      } (PNG, JPG, JPEG, WEBP)`;
    },
    urlDialogCurrentCount() {
      return this.urlDialogTarget === 'reference' ? this.imageRef.length : this.attachments.length;
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.resetState();
        this.applyConceptToMessage();
        this.$nextTick(() => {
          const textareaEl = this.$refs.messageInput;
          if (textareaEl) {
            textareaEl.scrollTop = 0;
          }
        });
      }
    },
    concept: {
      deep: true,
      handler() {
        if (this.value) this.applyConceptToMessage();
      },
    },
  },
  methods: {
    hasReadyInList(list) {
      return Array.isArray(list) && list.some(a => a && a.status === 'ready' && (a.remoteUrl || a.url));
    },

    buildConceptPrompt(concept) {
      // const title = String(concept?.title || '').trim();
      const description = String(concept?.description || '').trim();
      // const intro = `Giờ hãy tạo cho tôi ảnh theo concept "${title}". Tỉ lệ ảnh 1:1, text rõ ràng dễ hiểu, đúng chính tả ngữ pháp. Màu sắc hài hòa dễ nhìn phù hợp tệp đối tượng. Nhấn mạnh thể hiện rõ phần giá chỉ $24.95, có CTA SHOP NOW.`;
      // return description ? `${intro}\n\nConcept: \n${description}` : intro;
      return description;
    },

    applyConceptToMessage() {
      if (!this.concept) return;
      this.message = this.buildConceptPrompt(this.concept);
    },

    revokeList(list) {
      if (!Array.isArray(list)) return;
      list.forEach(a => {
        if (a?.localUrl) URL.revokeObjectURL(a.localUrl);
        if (a?.url && String(a.url).startsWith('blob:')) URL.revokeObjectURL(a.url);
      });
    },

    resetState() {
      this.revokeList(this.attachments);
      this.attachments = [];
      this.revokeList(this.imageRef);
      this.imageRef = [];
      this.showAllAttachments = false;
      this.message = '';
      this.urlDialogTarget = 'product';
      this.closePreview();
      this.showAddMediaUrlModal = false;
      this.showSelectProductImageModal = false;
      this.selectProductImageTarget = 'product';
    },

    onDialogInput(val) {
      this.$emit('input', !!val);
      if (!val) this.resetState();
    },

    cancel() {
      this.$emit('input', false);
      this.resetState();
    },

    triggerProductUpload() {
      const input = this.$refs.productFileInput;
      if (input) input.click();
    },

    triggerRefUpload() {
      const input = this.$refs.refFileInput;
      if (input) input.click();
    },

    onProductFilesSelected(e) {
      const list = e.target.files ? Array.from(e.target.files) : [];
      const file = list.find(f => this.isAcceptedFileType(f));
      e.target.value = '';
      if (!file) return;
      if (this.productSlotFilled) {
        this.attachments.forEach(a => this.removeAttachment(a.id));
      }
      this.addAttachment(file);
    },

    onRefFilesSelected(e) {
      const list = e.target.files ? Array.from(e.target.files) : [];
      const file = list.find(f => this.isAcceptedFileType(f));
      e.target.value = '';
      if (!file) return;
      if (this.refSlotFilled) {
        this.imageRef.forEach(a => this.removeRefAttachment(a.id));
      }
      this.addRefAttachment(file);
    },

    addRefAttachment(file) {
      const id = this.objectId();
      const localUrl = URL.createObjectURL(file);
      const item = {
        id,
        file,
        name: file.name,
        url: localUrl,
        localUrl,
        remoteUrl: '',
        status: 'uploading',
        isVideo: false,
      };
      this.imageRef.push(item);
      this.uploadRefAttachment(item);
    },

    async uploadRefAttachment(item) {
      try {
        const file = item?.file;
        if (!file) throw new Error('Missing file');
        if (file.size > this.maxFileSize) {
          const mb = Math.round(this.maxFileSize / 1024 / 1024);
          throw new Error(`File must be less than ${mb}MB`);
        }

        const ext = this.getFileExtension(file);
        const filename = `attachment-${item.id}.${ext}`;

        const resGetImage = await uploadService.get({ filename });
        const presignedUrl = resGetImage?.data?.presignedUrl;
        const imagePath = resGetImage?.data?.imagePath;
        if (!presignedUrl || !imagePath) throw new Error('Invalid upload response');

        await axios.put(presignedUrl, file, {
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
          },
        });

        const remoteUrl = imageHelpers.url(imagePath);
        item.remoteUrl = remoteUrl;
        item.url = remoteUrl;
        item.status = 'ready';

        if (item.localUrl) URL.revokeObjectURL(item.localUrl);
        item.localUrl = '';
      } catch {
        item.status = 'error';
      }
    },

    removeRefAttachment(id) {
      const idx = this.imageRef.findIndex(a => a.id === id);
      if (idx === -1) return;
      const att = this.imageRef[idx];
      if (att && this.preview.src && (att.url === this.preview.src || att.remoteUrl === this.preview.src)) {
        this.closePreview();
      }
      const [removed] = this.imageRef.splice(idx, 1);
      if (removed?.localUrl) URL.revokeObjectURL(removed.localUrl);
      if (removed?.url && String(removed.url).startsWith('blob:')) URL.revokeObjectURL(removed.url);
    },

    openAddMediaFromUrlDialog(target) {
      this.urlDialogTarget = target;
      this.showAddMediaUrlModal = true;
    },

    removeAttachment(id) {
      const att = this.attachments.find(a => a.id === id);
      if (att && this.preview.src && (att.url === this.preview.src || att.remoteUrl === this.preview.src)) {
        this.closePreview();
      }
      attachmentUploadMixin.methods.removeAttachment.call(this, id);
    },

    pushUrlToList(list, url) {
      list.push({
        id: this.objectId(),
        file: null,
        name: filenameFromMediaUrl(url, false),
        url,
        localUrl: '',
        remoteUrl: url,
        status: 'ready',
        isVideo: false,
      });
    },

    onSaveMediaFromUrlDialog(payload) {
      const items = Array.isArray(payload?.items) ? payload.items : [];
      const isRef = this.urlDialogTarget === 'reference';
      const list = isRef ? this.imageRef : this.attachments;

      // get first item url
      const url = items[0].url;
      this.pushUrlToList(list, url);
      this.showAllAttachments = false;
    },

    openPreview(att) {
      if (!att?.url || att.status !== 'ready') return;
      this.preview.src = att.url;
      this.preview.isVideo = false;
      this.preview.show = true;
    },

    closePreview() {
      this.preview.show = false;
      this.preview.src = '';
      this.preview.isVideo = false;
    },

    mapAttachmentsForSubmit(list) {
      return (Array.isArray(list) ? list : [])
        .filter(a => a && a.status === 'ready' && (a.remoteUrl || a.url))
        .map(a => ({
          id: a.id,
          name: a.name,
          url: a.remoteUrl || a.url,
          src: a.remoteUrl || a.url,
          remoteUrl: a.remoteUrl || a.url,
          status: a.status,
          isVideo: false,
        }));
    },

    onSubmit() {
      if (!this.canApply) return;
      this.closePreview();

      const payload = {
        mode: this.mode,
        message: this.message.trim(),
        attachments: this.mapAttachmentsForSubmit(this.attachments),
        image_ref: this.mapAttachmentsForSubmit(this.imageRef),
      };

      this.$emit('submit', payload);
      this.$emit('input', false);
      this.resetState();
    },

    openSelectProductImageDialog(target) {
      this.urlDialogTarget = target;
      this.selectProductImageTarget = this.urlDialogTarget;
      this.showSelectProductImageModal = true;
    },

    onSelectProductImage({ url }) {
      const imageUrl = String(url || '').trim();
      if (!imageUrl) return;
      const isRef = this.urlDialogTarget === 'reference';
      const list = isRef ? this.imageRef : this.attachments;
      this.pushUrlToList(list, imageUrl);
      this.showSelectProductImageModal = false;
      this.showAllAttachments = false;
    },
  },
};
</script>

<style scoped lang="scss">
.image-generation-dialog {
  position: relative;
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  --main-text-color: #1f1f1f;
  --main-background: #e9eef6;
}

.image-generation-dialog__panel {
  width: 100%;
  background: #fff;
  border-radius: 16px;
  /* padding: 16px 18px 18px; */
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  max-height: calc(100vh - 48px);
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &--concept {
    max-width: 880px;
  }

  &--edit {
    max-width: 880px;
  }

  &--redesign {
    max-width: 1000px;
  }
}

.image-generation-dialog__topbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
}

.image-generation-dialog__title {
  margin: 4px 0 0;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--main-text-color, #1f1f1f);
}

.image-generation-dialog__concept,
.image-generation-dialog__current-model {
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(25, 118, 210, 0.06);
  border: 1px solid rgba(25, 118, 210, 0.14);
}

.image-generation-dialog__concept-label {
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.5);
}

.image-generation-dialog__concept-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--main-text-color, #1f1f1f);
}

.image-generation-dialog__file-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.image-generation-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow: auto;
  padding: 0 16px;
}

.redesign-compare__hint {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: rgba(0, 0, 0, 0.55);
}

.redesign-compare {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 12px;
  align-items: stretch;

  &--edit {
    grid-template-columns: 1fr;
  }
}

.redesign-compare__column {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  padding: 14px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fafafa;
}

.redesign-compare__column-head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.redesign-compare__column-icon {
  flex-shrink: 0;
  margin-top: 2px;
  padding: 6px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);

  &--product {
    color: #1976d2;
  }

  &--ref {
    color: #7b1fa2;
  }
}

.redesign-compare__column-label {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--main-text-color, #1f1f1f);
}

.redesign-compare__column-desc {
  margin: 2px 0 0;
  font-size: 12px;
  line-height: 1.35;
  color: rgba(0, 0, 0, 0.5);
}

.redesign-compare__upload-zone {
  padding: 14px 12px;
}

.upload-zone__url-buttons {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.redesign-compare__url-btn {
  flex: 0 0 auto;
  align-self: center;
  text-transform: none;
  letter-spacing: 0;
  padding: 12px 16px !important;
  width: 100%;
  border: 1px solid var(--v-border-base);
  border-radius: 10px;
  background: #fafafa;
  font-size: 13px !important;
}

.image-generation-content__prompt-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--main-text-color);
  margin-bottom: 8px;
}

.redesign-compare__preview {
  display: flex;
  justify-content: center;
}

.redesign-compare__thumb-wrap {
  position: relative;
}

.redesign-compare__thumb {
  position: relative;
  width: 100%;
  max-width: 100px;
  aspect-ratio: 1;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: #f0f0f0;
  overflow: hidden;
  cursor: pointer;
  padding: 0;
  display: block;

  &:disabled {
    cursor: default;
    opacity: 0.85;
  }
}

.redesign-compare__thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.redesign-compare__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  border-radius: 12px;
  border: 1px dashed rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 0.6);
}

.redesign-compare__divider {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 48px;
}

.redesign-compare__vs {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.45);
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.image-generation-content__upload-toolbar {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.image-generation-content__upload-zone {
  flex: 1 1 auto;
  min-width: 0;
  border: 1.5px dashed rgba(0, 0, 0, 0.16);
  border-radius: 14px;
  padding: 18px 16px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  outline: none;
  background: #fafafa;

  &:hover,
  &:focus {
    border-color: var(--v-primary-base, #1976d2);
    background: rgba(25, 118, 210, 0.04);
  }

  .upload-zone__title {
    font-size: 14px;
    font-weight: 500;
    color: var(--main-text-color, #1f1f1f);
  }

  .upload-zone__subtitle {
    font-size: 12px;
    color: var(--main-text-color, #1f1f1f);
    opacity: 0.5;
  }
}

.image-generation-content__url-btn {
  flex: 0 0 auto;
  align-self: center;
  text-transform: none;
  letter-spacing: 0;
  padding: 12px 16px !important;
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  background: #fafafa;
}

.image-generation-content__attachments {
  flex: 0 0 auto;
}

.attachments-strip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  overflow-x: auto;
  overflow-y: hidden;
  padding-block: 4px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 6px;
  }
}

.attachment {
  position: relative;
  flex: 0 0 auto;
}

.attachment__thumb {
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #f0f0f0;
  overflow: hidden;
  cursor: pointer;
  padding: 0;
  display: grid;
  place-items: center;

  &:disabled {
    cursor: default;
    opacity: 0.85;
  }
}

.attachment__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.attachment__overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: grid;
  place-items: center;
}

.attachment__overlay--error {
  background: rgba(211, 47, 47, 0.65);
}

.attachment__remove {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #fff;
  cursor: pointer;
  display: grid;
  place-items: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
  z-index: 2;
  padding: 0;
}

.attachments-more {
  flex: 0 0 auto;
  height: 34px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px dashed rgba(0, 0, 0, 0.18);
  background: rgba(0, 0, 0, 0.03);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.3s ease;
  color: var(--main-text-color, #1f1f1f);

  &:hover {
    background: var(--main-background, #e9eef6);
  }
}

.image-generation-content__textarea {
  flex: 1 1 auto;
  min-height: 280px;
  width: 100%;
  border: 1.5px solid rgba(0, 0, 0, 0.1);
  border-radius: 14px;
  padding: 12px 14px;
  font-size: 14px;
  line-height: 1.55;
  resize: vertical;
  outline: none;
  font-family: inherit;
  color: var(--main-text-color, #1f1f1f);

  &:focus {
    border-color: var(--v-primary-base, #1976d2);
  }

  &--redesign {
    min-height: 200px;
  }
}

.image-generation-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px;
}

.image-generation-dialog__action-btn {
  min-width: 88px !important;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
}

@media (max-width: 640px) {
  .redesign-compare {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto;
  }

  .redesign-compare__divider {
    padding-top: 0;
  }

  .redesign-compare__vs {
    transform: rotate(90deg);
  }
}

::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 999px;
}

::-webkit-scrollbar-thumb {
  background: #d0d1d3;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #c8cbd0;
}
</style>
